// Update the ChatInput component
import React, { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import Button from '../button/button';
import styles from './chat-input.scss';

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const ChatInput = forwardRef(({ onSendMessage, disabled, value = '', onChange, isLightMode, placeholder }, ref) => {
  const textareaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const length = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    }
  }));

  // Auto-resize function
  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  // Auto-resize on value change
  useEffect(() => {
    autoResize();
  }, [value]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Remove disabled check - always allow sending
    if (value.trim()) {
      onSendMessage(value.trim());
    }
  };

const handleChange = (e) => {
  if (onChange) {
    onChange(e.target.value);
  }
  // Auto-resize
  if (textareaRef.current) {
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    
    // Show scrollbar if content exceeds max height
    if (textareaRef.current.scrollHeight > 120) {
      textareaRef.current.style.overflowY = 'auto';
    } else {
      textareaRef.current.style.overflowY = 'hidden';
    }
  }
};


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Remove disabled check - always allow sending
      if (value.trim()) {
        handleSubmit(e);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`${styles.chatInputForm} ${isLightMode ? styles.lightMode : ''}`}>
      <div className={styles.inputGroup}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder || "Ask me anything about your brand performance..."}
          disabled={disabled}
          className={`${styles.messageInput} ${disabled ? styles.disabled : ''}`}
          rows={1}
          style={{
            minHeight: '44px',
            maxHeight: '200px',
            resize: 'none',
            overflow: value.length > 500 ? 'auto' : 'hidden'
          }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={disabled || !value.trim()}
          icon={<SendIcon />}
          className={styles.sendButton}
        />
      </div>
    </form>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;
